import type { ReactNode } from "react";
import { Loader2, Inbox, AlertTriangle } from "lucide-react";
import { useAppContext } from "@/app/ContextProvider";
import { Skeleton } from "@/components/ui/skeleton";

/** Teljes oldalas töltő. */
export function PageLoader() {
  return (
    <div className="flex min-h-[40dvh] items-center justify-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Ismeretlen hiba történt.";
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
      <AlertTriangle className="size-6 text-destructive" />
      <p className="text-sm text-destructive">{message}</p>
    </div>
  );
}

export function EmptyState({ title, description, icon }: { title: string; description?: string; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-10 text-center">
      <div className="text-muted-foreground">{icon ?? <Inbox className="size-7" />}</div>
      <p className="font-medium">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

export function CardListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-28 w-full rounded-xl" />
      ))}
    </div>
  );
}

/** Ha nincs kiválasztott liga+torna, minden scope-olt oldal ezt mutatja. */
export function RequireContext({ children }: { children: ReactNode }) {
  const { isReady, isLoading, error } = useAppContext();
  if (isLoading) return <PageLoader />;
  if (error) return <ErrorState error={error} />;
  if (!isReady) {
    return (
      <EmptyState
        title="Válassz ligát és bajnokságot"
        description="A fejlécben található választókkal állítsd be, melyik ligát és tornát nézed."
      />
    );
  }
  return <>{children}</>;
}
